import * as React from "react";
import { cn } from "@/lib/utils";
import { TextArea, type TextAreaProps } from "@/components/ui/textarea";

// TextArea that grows its height to match the content. Measures scrollHeight whenever the value
// changes (controlled) or on user input (uncontrolled), with a minHeight derived from the
// standard `rows` prop so empty state still has a sensible floor. Shrinks back when content is
// deleted. Uses the same visual as the base TextArea -- just adds the height behaviour.
//
// Caller handles overflow by capping maxHeight if desired (via style/className). We don't impose
// one here because the review-and-approve / edit callsites want the content fully visible by
// default; they can clamp if a long paste gets out of hand.
export interface AutogrowTextAreaProps extends TextAreaProps {
  minRows?: number;
}

export const AutogrowTextArea = React.forwardRef<
  HTMLTextAreaElement,
  AutogrowTextAreaProps
>(
  (
    { className, style, minRows = 2, value, onChange, ...props },
    forwardedRef,
  ) => {
    const innerRef = React.useRef<HTMLTextAreaElement | null>(null);

    // Expose the ref both ways so parent code can still grab the element if needed, while we
    // retain our own handle for the measurement effect.
    React.useImperativeHandle(
      forwardedRef,
      () => innerRef.current as HTMLTextAreaElement,
    );

    const resize = React.useCallback(() => {
      const el = innerRef.current;
      if (!el) return;
      // Reset to auto first so shrinking works: scrollHeight only ever grows past the current
      // height, never below it. Then set to the content's measured height.
      el.style.height = "auto";
      el.style.height = `${el.scrollHeight}px`;
    }, []);

    // Re-measure when the controlled value changes. For uncontrolled textareas (no `value` prop)
    // the onInput handler below drives it.
    React.useEffect(() => {
      resize();
    }, [value, resize]);

    // Also measure on first mount so a prefilled default is sized correctly even before any
    // change event fires.
    React.useEffect(() => {
      resize();
    }, [resize]);

    const minHeight = `calc(${minRows} * 1.5em + 0.5rem)`;

    return (
      <TextArea
        {...props}
        ref={innerRef}
        value={value}
        onChange={(e) => {
          onChange?.(e);
          resize();
        }}
        onInput={resize}
        style={{ minHeight, resize: "none", overflow: "hidden", ...style }}
        className={cn("py-2 leading-relaxed", className)}
      />
    );
  },
);

AutogrowTextArea.displayName = "AutogrowTextArea";
